import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography, IconButton } from '@mui/material';
import { UploadFile, CheckCircle, Description, Image as ImageIcon, AutoStories, DeleteOutline } from '@mui/icons-material';

import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { listKnowledgeDocuments, processAndUploadDocument, deleteKnowledgeDocument } from '../../utils/server/ragUtils';
import { getAuthToken } from '../../utils/client/getAuthToken';
import { useAppTranslation } from '../../hooks/useAppTranslation';

export function KnowledgeBasePanel() {
    const { t, tr, lang } = useAppTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    const [isDeletingId, setIsDeletingId] = useState(null);
    const fileInputRef = useRef(null);

    const clearSelectedFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const loadDocuments = useCallback(async () => {
        setIsLoadingDocuments(true);
        try {
            const userAuthToken = await getAuthToken();
            if (!userAuthToken) {
                setDocuments([]);
                return;
            }

            const nextDocuments = await listKnowledgeDocuments(userAuthToken);
            setDocuments(Array.isArray(nextDocuments) ? nextDocuments : []);
        } catch (error) {
            console.error(error);
            setDocuments([]);
        } finally {
            setIsLoadingDocuments(false);
        }
    }, []);

    const handleDeleteDocument = async (documentId) => {
        if (isDeletingId) return;
        setIsDeletingId(documentId);
        try {
            const userAuthToken = await getAuthToken();
            const result = await deleteKnowledgeDocument(documentId, userAuthToken);
            if (result?.success) {
                showToastSuccess(t.kbDeleteSuccess);
                await loadDocuments();
            } else {
                showToastError(result?.message || t.kbDeleteFailed);
            }
        } catch(e) {
            showToastError(t.kbDeleteFailed);
        } finally {
            setIsDeletingId(null);
        }
    };

    const formatBytes = (value) => {
        const size = Number(value || 0);
        if (!Number.isFinite(size) || size <= 0) return '—';
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatDateTime = (value) => {
        if (!value) return '—';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '—';
        return parsed.toLocaleString(lang);
    };

    const getStatusMeta = (status) => {
        const normalizedStatus = String(status || '').toLowerCase();
        if (normalizedStatus === 'ready') {
            return { label: t.kbStatusReady, border: 'rgba(22, 163, 74, 0.35)', background: 'rgba(22, 163, 74, 0.12)', color: '#15803d' };
        }
        if (normalizedStatus === 'processing') {
            return { label: t.kbStatusProcessing, border: 'rgba(59, 130, 246, 0.35)', background: 'rgba(59, 130, 246, 0.12)', color: '#1d4ed8' };
        }
        if (normalizedStatus === 'failed') {
            return { label: t.kbStatusFailed, border: 'rgba(220, 38, 38, 0.35)', background: 'rgba(220, 38, 38, 0.12)', color: '#b91c1c' };
        }
        return { label: normalizedStatus || t.kbStatusUnknown, border: 'rgba(148, 163, 184, 0.35)', background: 'rgba(148, 163, 184, 0.12)', color: '#475569' };
    };

    useEffect(() => {
        loadDocuments();

        const handleScopeChange = () => {
            if (!isLoading) {
                clearSelectedFile();
            }
            loadDocuments();
        };

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, [isLoading, loadDocuments]);

    const handleFileSelect = (event) => {
        if (!event.target.files || !event.target.files[0]) return;

        const file = event.target.files[0];
        const normalizedFileName = String(file.name || '').toLowerCase();
        const allowedTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/msword',
            'text/plain',
            'text/markdown',
            'image/jpeg',
            'image/png',
            'image/webp',
        ];
        const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.jpg', '.jpeg', '.png', '.webp'];
        const hasAllowedExtension = allowedExtensions.some((extension) => normalizedFileName.endsWith(extension));

        if (!allowedTypes.includes(file.type) && !hasAllowedExtension) {
            showToastError(t.kbUnsupportedFileType);
            event.target.value = '';
            return;
        }

        setSelectedFile(file);
    };

    const handleUpload = async () => {
        if (!selectedFile || isLoading) return;

        setIsLoading(true);
        try {
            const userAuthToken = await getAuthToken();
            if (!userAuthToken) {
                showToastError(t.pleaseLogin);
                return;
            }

            const formData = new FormData();
            formData.append('file', selectedFile);

            const result = await processAndUploadDocument(formData, userAuthToken);

            if (result?.success) {
                showToastSuccess(result.message);
                clearSelectedFile();
                await loadDocuments();
            } else {
                showToastError(result?.message || t.kbUploadFailed);
            }
        } catch (error) {
            console.error(error);
            showToastError(t.kbUploadRetry);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2.25, md: 3 },
                borderRadius: '28px',
                border: '1px solid var(--border-color)',
                background: 'var(--surface-gradient)',
                boxShadow: '0 24px 48px rgba(15, 23, 42, 0.08)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AutoStories sx={{ color: 'var(--accent-color)' }} />
                <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                    {t.knowledgeBaseTitle}
                </Typography>
            </Box>
            <Typography variant='body2' sx={{ color: 'var(--text-muted)', mb: 2.5, lineHeight: 1.7 }}>
                {t.knowledgeBaseDesc}
                <br />
                <strong>{t.knowledgeBaseSupported}</strong>
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    p: { xs: 2.5, md: 4 },
                    borderRadius: '24px',
                    border: '2px dashed var(--border-color)',
                    background: 'var(--surface-gradient-soft)',
                }}
            >
                <Box
                    sx={{
                        width: 68,
                        height: 68,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '22px',
                        background: 'linear-gradient(135deg, var(--accent-soft-bg) 0%, var(--accent-soft-bg-2) 100%)',
                    }}
                >
                    <UploadFile fontSize='large' sx={{ color: 'var(--accent-color)' }} />
                </Box>

                <Button
                    component='label'
                    variant='outlined'
                    disabled={isLoading}
                    sx={{
                        borderRadius: '16px',
                        px: 2.5,
                        py: 1.05,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderColor: 'var(--border-color)',
                        color: 'var(--text-soft)',
                        backgroundColor: 'var(--surface-fill-strong)',
                        '&.Mui-disabled': {
                            color: 'rgba(255,255,255,0.72)',
                            background: 'var(--button-disabled-bg)',
                        },
                    }}
                >
                    {t.chooseFile}
                    <input
                        ref={fileInputRef}
                        type='file'
                        hidden
                        accept='.pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png,.webp'
                        onChange={handleFileSelect}
                    />
                </Button>

                {selectedFile && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.25,
                            py: 1,
                            borderRadius: '14px',
                            backgroundColor: 'var(--surface-fill-strong)',
                            border: '1px solid var(--border-color)',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                        }}
                    >
                        {selectedFile.type.startsWith('image/') ? <ImageIcon sx={{ color: 'var(--accent-color)' }} /> : <Description sx={{ color: 'var(--accent-color)' }} />}
                        <Typography variant='body1' sx={{ color: 'var(--text-strong)', fontWeight: 600, wordBreak: 'break-word' }}>
                            {selectedFile.name}
                        </Typography>
                        <CheckCircle color='success' fontSize='small' />
                        <Button size='small' variant='text' onClick={clearSelectedFile} sx={{ textTransform: 'none', fontWeight: 700 }}>
                            {t.remove}
                        </Button>
                    </Box>
                )}

                <Button
                    variant='contained'
                    disabled={!selectedFile || isLoading}
                    onClick={handleUpload}
                    startIcon={isLoading ? <CircularProgress size={20} color='inherit' /> : null}
                    sx={{
                        minWidth: 180,
                        borderRadius: '16px',
                        px: 2.5,
                        py: 1.15,
                        textTransform: 'none',
                        fontWeight: 800,
                        background: 'var(--accent-gradient)',
                        boxShadow: '0 16px 32px var(--accent-shadow)',
                        '&.Mui-disabled': {
                            color: 'rgba(255,255,255,0.72)',
                            background: 'var(--button-disabled-bg)',
                        },
                    }}
                >
                    {isLoading ? t.analyzingAndSaving : t.upload}
                </Button>
            </Box>

            <Box
                sx={{
                    mt: 2.5,
                    borderRadius: '24px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--surface-gradient-soft)',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: { xs: 2, md: 2.5 },
                        py: 1.6,
                        borderBottom: '1px solid var(--border-color)',
                    }}
                >
                    <Box>
                        <Typography variant='h6' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.kbStoredDocumentsTitle}
                        </Typography>
                        <Typography variant='body2' sx={{ color: 'var(--text-muted)', mt: 0.25 }}>
                            {t.kbStoredDocumentsDescription}
                        </Typography>
                    </Box>
                    <Button
                        size='small'
                        variant='outlined'
                        onClick={loadDocuments}
                        disabled={isLoadingDocuments}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: 'var(--border-color)',
                            color: 'var(--text-soft)',
                        }}
                    >
                        {isLoadingDocuments ? t.refreshing : t.refresh}
                    </Button>
                </Box>

                <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.75 }}>
                    {isLoadingDocuments ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={18} />
                            <Typography variant='body2' sx={{ color: 'var(--text-muted)' }}>
                                {t.kbLoadingDocuments}
                            </Typography>
                        </Box>
                    ) : documents.length === 0 ? (
                        <Typography variant='body2' sx={{ color: 'var(--text-muted)' }}>
                            {t.kbNoDocuments}
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                            {documents.map((document) => {
                                const status = getStatusMeta(document.status);
                                const isImageDocument = String(document.mimeType || '').startsWith('image/');

                                return (
                                    <Box
                                        key={document.id || `${document.filename}-${document.updatedAt || document.createdAt || ''}`}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) auto auto auto' },
                                            gap: 1.2,
                                            alignItems: { xs: 'flex-start', md: 'center' },
                                            px: 1.4,
                                            py: 1.2,
                                            borderRadius: '16px',
                                            border: '1px solid var(--border-color)',
                                            backgroundColor: 'var(--surface-fill-strong)',
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {isImageDocument ? <ImageIcon sx={{ color: 'var(--accent-color)' }} /> : <Description sx={{ color: 'var(--accent-color)' }} />}
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant='body1' sx={{ color: 'var(--text-strong)', fontWeight: 700, wordBreak: 'break-word' }}>
                                                    {document.filename || t.kbUnnamedDocument}
                                                </Typography>
                                                <Typography variant='caption' sx={{ color: 'var(--text-muted)' }}>
                                                    {document.mimeType || t.kbUnknownType}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                justifySelf: { xs: 'flex-start', md: 'center' },
                                                px: 1,
                                                py: 0.45,
                                                borderRadius: 99,
                                                border: `1px solid ${status.border}`,
                                                backgroundColor: status.background,
                                                color: status.color,
                                                fontSize: '0.78rem',
                                                fontWeight: 800,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {status.label}
                                        </Box>

                                        <Typography variant='body2' sx={{ color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                                            {formatBytes(document.sizeBytes)}
                                        </Typography>

                                        <Box sx={{ justifySelf: { xs: 'flex-start', md: 'end' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant='body2' sx={{ color: 'var(--text-soft)', fontWeight: 600 }}>
                                                    {tr('kbChunkCount', { count: document.processedChunkCount ?? 0 })}
                                                </Typography>
                                                <Typography variant='caption' sx={{ color: 'var(--text-muted)' }}>
                                                    {tr('kbUpdatedAt', { date: formatDateTime(document.updatedAt || document.createdAt) })}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteDocument(document.id)}
                                                disabled={isDeletingId === document.id}
                                                sx={{ backgroundColor: 'rgba(220, 38, 38, 0.08)', '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.16)' } }}
                                            >
                                                {isDeletingId === document.id ? <CircularProgress size={16} color="error" /> : <DeleteOutline fontSize="small" />}
                                            </IconButton>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

        </Paper>
    );
}